import React, { useEffect, useState } from "react";
import { FormControl, InputAdornment, InputLabel, MenuItem, Select } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import { useSelector } from "react-redux";
import {
  GetProfsForDropdownCall,
  GetUniversityProfsCall,
} from "../../redux/api/professor.api";
import {
  GetUniversitiesCall,
  GetUniversityUsersCall,
} from "../../redux/api/university.api";

function normalizeRole(role) {
  return String(role || "")
    .toLowerCase()
    .trim()
    .replace(/[\s-]+/g, "_");
}

function asUserList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.rows)) return payload.rows;
  return [];
}

function isQuizCapableRole(role) {
  const r = normalizeRole(role);
  return r === "prof" || r === "professor" || r === "university_admin";
}

function dedupeUsers(lists) {
  const map = new Map();
  lists.flat().forEach((u) => {
    if (!u?.id) return;
    map.set(String(u.id), u);
  });
  return Array.from(map.values()).sort((a, b) => {
    const an = `${a.first_name || ""} ${a.last_name || ""}`.trim() || a.email || "";
    const bn = `${b.first_name || ""} ${b.last_name || ""}`.trim() || b.email || "";
    return an.localeCompare(bn);
  });
}

function displayName(p) {
  return (
    [p?.first_name, p?.last_name].filter(Boolean).join(" ") ||
    p?.email ||
    p?.id ||
    "—"
  );
}

/**
 * Shows a "Select professor" dropdown when the current user is admin or university_admin.
 * Optional: include "My content" / "All professors" (value "") so they can view own or all data.
 */
export default function ProfSelector({
  value,
  onChange,
  sx = {},
  includeMyContent = true,
  onProfsLoaded,
}) {
  const theme = useTheme();
  const auth = useSelector((state) => state?.auth || {});
  const role = normalizeRole(auth?.role);
  const authUniversityId =
    auth?.university_id ?? auth?.universityInfo?.id ?? auth?.universityInfo?.university_id ?? null;
  const [profs, setProfs] = useState([]);
  const [loading, setLoading] = useState(false);

  const showSelector = role === "admin" || role === "university_admin" || role === "university";
  const isUniAdmin = role === "university_admin" || role === "university";
  // Empty value = viewer's own library (create/edit). Pick a professor to filter.
  const emptyLabel = "My content";

  useEffect(() => {
    if (!showSelector) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        let universityId = authUniversityId ? String(authUniversityId) : null;

        // If auth is missing university_id, resolve it from the universities list.
        if (!universityId && isUniAdmin) {
          const unis = await GetUniversitiesCall();
          universityId = unis?.[0]?.id ? String(unis[0].id) : null;
        }

        const bags = [];

        // 1) JWT-scoped dropdown endpoint
        bags.push(asUserList(await GetProfsForDropdownCall()));

        if (universityId) {
          // 2) Same endpoint as Professors page
          bags.push(asUserList(await GetUniversityProfsCall(universityId)));

          // 3) Broader roster: any quiz-capable university member
          const roster = asUserList(await GetUniversityUsersCall(universityId));
          bags.push(roster.filter((u) => isQuizCapableRole(u.role)));

          // 4) Explicit teaching-staff query (profs + university admins)
          bags.push(
            asUserList(
              await GetUniversityUsersCall(universityId, "prof,university_admin")
            )
          );
        }

        let list = dedupeUsers(bags);

        // Keep professors and university admins who may own quizzes; drop pure students.
        list = list.filter((u) => isQuizCapableRole(u.role) || !u.role);

        if (!cancelled) {
          setProfs(list);
          onProfsLoaded?.(list);
        }
      } catch (_) {
        if (!cancelled) {
          setProfs([]);
          onProfsLoaded?.([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [showSelector, role, authUniversityId, isUniAdmin]);

  if (!showSelector) return null;

  const displayValue = value ?? "";

  return (
    <FormControl
      size="small"
      sx={{
        minWidth: 240,
        "& .MuiOutlinedInput-root": {
          minHeight: 44,
          borderRadius: 2.5,
          bgcolor: theme.palette.background.paper || "#FFFFFF",
          boxShadow: "0 4px 14px rgba(15, 23, 42, 0.06)",
          transition: "box-shadow 0.2s ease, border-color 0.2s ease",
          "& fieldset": {
            borderColor: "rgba(148, 163, 184, 0.35)",
          },
          "&:hover fieldset": {
            borderColor: alpha(theme.palette.primary.main, 0.45),
          },
          "&.Mui-focused": {
            boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.16)}`,
            "& fieldset": {
              borderColor: theme.palette.primary.main,
              borderWidth: 1,
            },
          },
        },
        "& .MuiInputLabel-root": {
          fontWeight: 600,
          color: theme.palette.text.secondary,
        },
        ...sx,
      }}
    >
      <InputLabel id="prof-selector-label" shrink>
        Professor
      </InputLabel>
      <Select
        labelId="prof-selector-label"
        label="Professor"
        value={displayValue}
        displayEmpty
        renderValue={(selected) => {
          if (!selected) {
            return includeMyContent ? emptyLabel : "Select professor";
          }
          const p = profs.find((x) => String(x.id) === String(selected));
          if (!p) return String(selected);
          return displayName(p);
        }}
        onChange={(e) => onChange(e.target.value)}
        disabled={loading}
        notched
        startAdornment={
          <InputAdornment position="start">
            <PersonOutlineRoundedIcon
              sx={{
                fontSize: 20,
                color: theme.palette.primary.main,
                opacity: 0.9,
              }}
            />
          </InputAdornment>
        }
        MenuProps={{
          PaperProps: {
            sx: {
              borderRadius: 2.5,
              mt: 1,
              border: "1px solid rgba(148, 163, 184, 0.2)",
              boxShadow: "0 12px 32px rgba(15, 23, 42, 0.12)",
            },
          },
        }}
      >
        {includeMyContent && (
          <MenuItem value="" sx={{ borderRadius: 1.5, mx: 0.5, my: 0.25 }}>
            {emptyLabel}
          </MenuItem>
        )}
        {profs.map((p) => (
          <MenuItem
            key={p.id}
            value={p.id}
            sx={{ borderRadius: 1.5, mx: 0.5, my: 0.25 }}
          >
            {displayName(p)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
